import _ from "lodash";
import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { Tabs } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import {
  TAB_TYPES,
  useParticipantsModal,
} from "../../context/ParticipantsModalContext";

/**
 * Tab bar for switching between Grades, Courses, Students, Family Members, and Staff Members.
 * Grades and Courses tabs are shown conditionally based on event associations.
 * Reads/writes activeTab from ParticipantsModalContext.
 */
const ParticipantTabs = ({
  hasGrades,
  hasCourses,
  gradesCount,
  coursesCount,
  testId,
}) => {
  const { t } = useI18n(["calendar", "common"]);
  const {
    activeTab,
    setActiveTab,
    staffCount,
    studentCount,
    familyCount,
    sharedWith,
  } = useParticipantsModal();

  const isSharedWithStudents = _.includes(sharedWith, "STUDENT");
  const isSharedWithFamily = _.includes(sharedWith, "PARENT");
  const isSharedWithStaff = _.includes(sharedWith, "STAFF");

  const tabOptions = _.compact([
    hasGrades && {
      value: TAB_TYPES.GRADES,
      label: `${t("common:grade_plural")} (${gradesCount})`,
    },
    hasCourses && {
      value: TAB_TYPES.COURSES,
      label: `${t("common:courses")} (${coursesCount})`,
    },
    isSharedWithStudents && {
      value: TAB_TYPES.STUDENTS,
      label: `${t("common:student_plural")} (${studentCount})`,
    },
    isSharedWithFamily && {
      value: TAB_TYPES.FAMILY,
      label: `${t("common:family_member_plural")} (${familyCount})`,
    },
    isSharedWithStaff && {
      value: TAB_TYPES.STAFF,
      label: `${t("common:staff_members_plural")} (${staffCount})`,
    },
  ]);

  const handleTabChange = useCallback(
    value => {
      setActiveTab(value);
    },
    [setActiveTab]
  );

  if (_.size(tabOptions) === 1) {
    return null;
  }

  return (
    <div>
      <Tabs
        dsVersion={"2.0"}
        options={tabOptions}
        value={activeTab}
        onChange={handleTabChange}
        type={"fixed"}
        variant={"inline"}
        hasSeparator={true}
        size={"medium"}
        testId={testId}
      />
    </div>
  );
};

ParticipantTabs.propTypes = {
  hasGrades: PropTypes.bool,
  hasCourses: PropTypes.bool,
  gradesCount: PropTypes.number,
  coursesCount: PropTypes.number,
  testId: PropTypes.string,
};

ParticipantTabs.defaultProps = {
  hasGrades: false,
  hasCourses: false,
  gradesCount: 0,
  coursesCount: 0,
  testId: "",
};

export default ParticipantTabs;
